import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { saveComment } from '../services/cmsApi';

export default function CommentForm({ onCommentAdded }) {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    job: '',
    comment: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name.trim() || !formData.job.trim() || !formData.comment.trim()) {
      setMessage({ type: 'error', text: t('comments.form.error') });
      return;
    }

    setIsSubmitting(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await saveComment(formData, language);
      
      if (response && (response.status === 1 || response.success)) {
        setMessage({ type: 'success', text: t('comments.form.success') });
        setFormData({ name: '', job: '', comment: '' });
        
        // Notify parent component that a new comment was added
        if (onCommentAdded) {
          onCommentAdded();
        }
      } else {
        setMessage({ type: 'error', text: t('comments.form.error') });
      }
    } catch (error) {
      console.error('Error saving comment:', error);
      setMessage({ 
        type: 'error', 
        text: error.message || t('comments.form.error') 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-orange-50/50 to-white rounded-2xl p-6 md:p-8 border border-orange-100/60 shadow-lg">
      <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 text-center">
        {t('comments.form.comment')}
      </h3>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              {t('comments.form.name')}
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder={t('comments.placeholder.name')}
              className="w-full px-4 py-3 bg-white/80 border border-orange-200/50 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#FF8500] focus:border-transparent transition-all duration-200"
              required
            />
          </div>
          
          <div>
            <label htmlFor="job" className="block text-sm font-medium text-gray-700 mb-2">
              {t('comments.form.job')}
            </label>
            <input
              type="text"
              id="job"
              name="job"
              value={formData.job}
              onChange={handleInputChange}
              placeholder={t('comments.placeholder.job')}
              className="w-full px-4 py-3 bg-white/80 border border-orange-200/50 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#FF8500] focus:border-transparent transition-all duration-200"
              required
            />
          </div>
        </div>
        
        <div>
          <label htmlFor="comment" className="block text-sm font-medium text-gray-700 mb-2">
            {t('comments.form.comment')}
          </label>
          <textarea
            id="comment"
            name="comment"
            value={formData.comment}
            onChange={handleInputChange}
            placeholder={t('comments.placeholder.comment')}
            rows={4}
            className="w-full px-4 py-3 bg-white/80 border border-orange-200/50 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#FF8500] focus:border-transparent transition-all duration-200 resize-none"
            required
          />
        </div>
        
        {message.text && (
          <div className={`p-3 rounded-lg text-sm ${
            message.type === 'success' 
              ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
              : 'bg-red-500/20 text-red-400 border border-red-500/30'
          }`}>
            {message.text}
          </div>
        )}
        
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-gradient-to-r from-[#FF8500] to-[#FF6B00] hover:from-[#FF6B00] hover:to-[#FF8500] text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-lg hover:shadow-xl"
        >
          {isSubmitting ? t('comments.form.sending') : t('comments.form.send')}
        </button>
      </form>
    </div>
  );
}

